import React, { useState, useEffect, useMemo, useCallback } from 'react';

// ============================================
// 多言語対応（i18n）
// ============================================
const translations = {
  en: {
    siteName: 'Presentation Materials',
    tagline: 'Free illustrations for your presentations',
    searchPlaceholder: 'Search illustrations...',
    allCategories: 'All',
    categories: {
      people: 'People',
      business: 'Business',
      technology: 'Technology',
      mark: 'Mark',
      education: 'Education',
      health: 'Health',
      life: 'Life',
      building: 'Building',
      transportation: 'Transportation',
      nature: 'Nature',
      foods: 'Foods',
      fashion: 'Fashion',
      entertainment: 'Entertainment',
      other: 'Other',
    },
    download: 'Download',
    copyToClipboard: 'Copy to Clipboard',
    copied: 'Copied!',
    downloadFormats: 'Download Formats',
    noResults: 'No illustrations found',
    tryDifferent: 'Try different keywords or browse categories',
    loading: 'Loading...',
    footer: 'Free for personal and commercial use',
    resultsCount: '{count} illustrations found',
    resultsRange: '{start}-{end} of illustrations',
    totalResultsCount: '{count} illustrations in total',
    tagSearchLabel: 'Tag "{tag}"',
    genreSearchLabel: '{genre}',
    quickCopy: 'Quick Copy',
    pasteDirectly: 'Paste directly into your application',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'Copy to Clipboard',
    prevPage: 'Previous',
    nextPage: 'Next',
    like: 'Like',
    dislike: 'Dislike',
    thankYouForFeedback: 'Thank you for your feedback!',
  },
  ja: {
    siteName: 'Presentation Materials',
    tagline: 'プレゼンテーション用フリーイラスト',
    searchPlaceholder: 'イラストを検索...',
    allCategories: 'すべて',
    categories: {
      people: '人物',
      business: 'ビジネス',
      technology: 'テクノロジー',
      mark: 'マーク',
      education: '教育',
      health: '健康',
      life: '生活',
      building: '建物',
      transportation: '交通',
      nature: '自然',
      foods: '食べ物',
      fashion: 'ファッション',
      entertainment: 'エンターテイメント',
      other: 'その他',
    },
    download: 'ダウンロード',
    copyToClipboard: 'クリップボードにコピー',
    copied: 'コピーしました！',
    downloadFormats: 'ダウンロード形式',
    noResults: 'イラストが見つかりません',
    tryDifferent: '別のキーワードで検索するか、カテゴリを選択してください',
    loading: '読み込み中...',
    footer: '個人・商用利用可能',
    resultsCount: '{count}件のイラスト',
    resultsRange: '{start}～{end}件目',
    totalResultsCount: '全{count}件',
    tagSearchLabel: 'タグ「{tag}」',
    genreSearchLabel: '{genre}',
    quickCopy: 'クイックコピー',
    pasteDirectly: 'アプリケーションに直接貼り付け可能',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'クリップボードにコピー',
    prevPage: '前へ',
    nextPage: '次へ',
    like: 'いいね',
    dislike: 'イマイチ',
    thankYouForFeedback: 'フィードバックありがとうございます！',
  },
  'zh-CN': {
    siteName: 'Presentation Materials',
    tagline: '免费演示文稿插图',
    searchPlaceholder: '搜索插图...',
    allCategories: '全部',
    categories: {
      people: '人物',
      business: '商业',
      technology: '技术',
      mark: '标志',
      education: '教育',
      health: '健康',
      life: '生活',
      building: '建筑',
      transportation: '交通',
      nature: '自然',
      foods: '食物',
      fashion: '时尚',
      entertainment: '娱乐',
      other: '其他',
    },
    download: '下载',
    copyToClipboard: '复制到剪贴板',
    copied: '已复制！',
    downloadFormats: '下载格式',
    noResults: '未找到插图',
    tryDifferent: '尝试其他关键词或浏览类别',
    loading: '加载中...',
    footer: '个人和商业用途免费',
    resultsCount: '找到 {count} 个插图',
    resultsRange: '第 {start}-{end} 个',
    totalResultsCount: '共 {count} 个插图',
    tagSearchLabel: '标签「{tag}」',
    genreSearchLabel: '{genre}',
    quickCopy: '快速复制',
    pasteDirectly: '直接粘贴到应用程序',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: '复制到剪贴板',
    prevPage: '上一页',
    nextPage: '下一页',
    like: '赞',
    dislike: '不喜欢',
    thankYouForFeedback: '感谢您的反馈！',
  },
  ko: {
    siteName: 'Presentation Materials',
    tagline: '프레젠테이션용 무료 일러스트',
    searchPlaceholder: '일러스트 검색...',
    allCategories: '전체',
    categories: {
      people: '인물',
      business: '비즈니스',
      technology: '기술',
      mark: '마크',
      education: '교육',
      health: '건강',
      life: '생활',
      building: '건물',
      transportation: '교통',
      nature: '자연',
      foods: '음식',
      fashion: '패션',
      entertainment: '엔터테인먼트',
      other: '기타',
    },
    download: '다운로드',
    copyToClipboard: '클립보드에 복사',
    copied: '복사됨!',
    downloadFormats: '다운로드 형식',
    noResults: '일러스트를 찾을 수 없습니다',
    tryDifferent: '다른 키워드로 검색하거나 카테고리를 탐색하세요',
    loading: '로딩 중...',
    footer: '개인 및 상업적 사용 무료',
    resultsCount: '{count}개의 일러스트',
    resultsRange: '{start}-{end}번째',
    totalResultsCount: '총 {count}개',
    tagSearchLabel: '태그 "{tag}"',
    genreSearchLabel: '{genre}',
    quickCopy: '빠른 복사',
    pasteDirectly: '앱에 바로 붙여넣기',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: '클립보드에 복사',
    prevPage: '이전',
    nextPage: '다음',
    like: '좋아요',
    dislike: '별로예요',
    thankYouForFeedback: '피드백 감사합니다!',
  },
  de: {
    siteName: 'Presentation Materials',
    tagline: 'Kostenlose Illustrationen für Präsentationen',
    searchPlaceholder: 'Illustrationen suchen...',
    allCategories: 'Alle',
    categories: {
      people: 'Menschen',
      business: 'Geschäft',
      technology: 'Technologie',
      mark: 'Zeichen',
      education: 'Bildung',
      health: 'Gesundheit',
      life: 'Leben',
      building: 'Gebäude',
      transportation: 'Verkehr',
      nature: 'Natur',
      foods: 'Essen',
      fashion: 'Mode',
      entertainment: 'Unterhaltung',
      other: 'Sonstiges',
    },
    download: 'Herunterladen',
    copyToClipboard: 'In Zwischenablage kopieren',
    copied: 'Kopiert!',
    downloadFormats: 'Download-Formate',
    noResults: 'Keine Illustrationen gefunden',
    tryDifferent: 'Versuchen Sie andere Stichwörter oder durchsuchen Sie Kategorien',
    loading: 'Laden...',
    footer: 'Kostenlos für private und kommerzielle Nutzung',
    resultsCount: '{count} Illustrationen gefunden',
    resultsRange: '{start}-{end} von',
    totalResultsCount: '{count} Illustrationen insgesamt',
    tagSearchLabel: 'Tag „{tag}"',
    genreSearchLabel: '{genre}',
    quickCopy: 'Schnellkopie',
    pasteDirectly: 'Direkt in die App einfügen',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'In Zwischenablage kopieren',
    prevPage: 'Zurück',
    nextPage: 'Weiter',
    like: 'Gefällt mir',
    dislike: 'Gefällt mir nicht',
    thankYouForFeedback: 'Vielen Dank für Ihr Feedback!',
  },
  fr: {
    siteName: 'Presentation Materials',
    tagline: 'Illustrations gratuites pour vos présentations',
    searchPlaceholder: 'Rechercher des illustrations...',
    allCategories: 'Tout',
    categories: {
      people: 'Personnes',
      business: 'Affaires',
      technology: 'Technologie',
      mark: 'Symboles',
      education: 'Éducation',
      health: 'Santé',
      life: 'Vie',
      building: 'Bâtiments',
      transportation: 'Transport',
      nature: 'Nature',
      foods: 'Nourriture',
      fashion: 'Mode',
      entertainment: 'Divertissement',
      other: 'Autres',
    },
    download: 'Télécharger',
    copyToClipboard: 'Copier dans le presse-papiers',
    copied: 'Copié !',
    downloadFormats: 'Formats de téléchargement',
    noResults: 'Aucune illustration trouvée',
    tryDifferent: 'Essayez d\'autres mots-clés ou parcourez les catégories',
    loading: 'Chargement...',
    footer: 'Gratuit pour usage personnel et commercial',
    resultsCount: '{count} illustrations trouvées',
    resultsRange: '{start}-{end} sur',
    totalResultsCount: '{count} illustrations au total',
    tagSearchLabel: 'Tag « {tag} »',
    genreSearchLabel: '{genre}',
    quickCopy: 'Copie rapide',
    pasteDirectly: 'Coller directement dans l\'application',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'Copier dans le presse-papiers',
    prevPage: 'Précédent',
    nextPage: 'Suivant',
    like: 'J\'aime',
    dislike: 'Je n\'aime pas',
    thankYouForFeedback: 'Merci pour votre retour !',
  },
  es: {
    siteName: 'Presentation Materials',
    tagline: 'Ilustraciones gratuitas para presentaciones',
    searchPlaceholder: 'Buscar ilustraciones...',
    allCategories: 'Todos',
    categories: {
      people: 'Personas',
      business: 'Negocios',
      technology: 'Tecnología',
      mark: 'Símbolos',
      education: 'Educación',
      health: 'Salud',
      life: 'Vida',
      building: 'Edificios',
      transportation: 'Transporte',
      nature: 'Naturaleza',
      foods: 'Alimentos',
      fashion: 'Moda',
      entertainment: 'Entretenimiento',
      other: 'Otros',
    },
    download: 'Descargar',
    copyToClipboard: 'Copiar al portapapeles',
    copied: '¡Copiado!',
    downloadFormats: 'Formatos de descarga',
    noResults: 'No se encontraron ilustraciones',
    tryDifferent: 'Intente con otras palabras clave o explore categorías',
    loading: 'Cargando...',
    footer: 'Gratis para uso personal y comercial',
    resultsCount: '{count} ilustraciones encontradas',
    resultsRange: '{start}-{end} de',
    totalResultsCount: '{count} ilustraciones en total',
    tagSearchLabel: 'Etiqueta "{tag}"',
    genreSearchLabel: '{genre}',
    quickCopy: 'Copia rápida',
    pasteDirectly: 'Pegar directamente en la aplicación',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'Copiar al portapapeles',
    prevPage: 'Anterior',
    nextPage: 'Siguiente',
    like: 'Me gusta',
    dislike: 'No me gusta',
    thankYouForFeedback: '¡Gracias por tu opinión!',
  },
  pt: {
    siteName: 'Presentation Materials',
    tagline: 'Ilustrações gratuitas para apresentações',
    searchPlaceholder: 'Pesquisar ilustrações...',
    allCategories: 'Todos',
    categories: {
      people: 'Pessoas',
      business: 'Negócios',
      technology: 'Tecnologia',
      mark: 'Símbolos',
      education: 'Educação',
      health: 'Saúde',
      life: 'Vida',
      building: 'Edifícios',
      transportation: 'Transporte',
      nature: 'Natureza',
      foods: 'Alimentos',
      fashion: 'Moda',
      entertainment: 'Entretenimento',
      other: 'Outros',
    },
    download: 'Baixar',
    copyToClipboard: 'Copiar para área de transferência',
    copied: 'Copiado!',
    downloadFormats: 'Formatos de download',
    noResults: 'Nenhuma ilustração encontrada',
    tryDifferent: 'Tente outras palavras-chave ou navegue pelas categorias',
    loading: 'Carregando...',
    footer: 'Grátis para uso pessoal e comercial',
    resultsCount: '{count} ilustrações encontradas',
    resultsRange: '{start}-{end} de',
    totalResultsCount: '{count} ilustrações no total',
    tagSearchLabel: 'Tag "{tag}"',
    genreSearchLabel: '{genre}',
    quickCopy: 'Cópia rápida',
    pasteDirectly: 'Cole diretamente no aplicativo',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'Copiar para área de transferência',
    prevPage: 'Anterior',
    nextPage: 'Próximo',
    like: 'Gostei',
    dislike: 'Não gostei',
    thankYouForFeedback: 'Obrigado pelo seu feedback!',
  },
  hi: {
    siteName: 'Presentation Materials',
    tagline: 'प्रस्तुतियों के लिए मुफ्त चित्र',
    searchPlaceholder: 'चित्र खोजें...',
    allCategories: 'सभी',
    categories: {
      people: 'लोग',
      business: 'व्यापार',
      technology: 'प्रौद्योगिकी',
      mark: 'चिह्न',
      education: 'शिक्षा',
      health: 'स्वास्थ्य',
      life: 'जीवन',
      building: 'भवन',
      transportation: 'परिवहन',
      nature: 'प्रकृति',
      foods: 'खाद्य',
      fashion: 'फैशन',
      entertainment: 'मनोरंजन',
      other: 'अन्य',
    },
    download: 'डाउनलोड',
    copyToClipboard: 'क्लिपबोर्ड पर कॉपी करें',
    copied: 'कॉपी हो गया!',
    downloadFormats: 'डाउनलोड प्रारूप',
    noResults: 'कोई चित्र नहीं मिला',
    tryDifferent: 'अन्य कीवर्ड आज़माएं या श्रेणियां ब्राउज़ करें',
    loading: 'लोड हो रहा है...',
    footer: 'व्यक्तिगत और व्यावसायिक उपयोग के लिए मुफ्त',
    resultsCount: '{count} चित्र मिले',
    resultsRange: '{start}-{end}वां',
    totalResultsCount: 'कुल {count} चित्र',
    tagSearchLabel: 'टैग "{tag}"',
    genreSearchLabel: '{genre}',
    quickCopy: 'त्वरित कॉपी',
    pasteDirectly: 'सीधे एप्लिकेशन में पेस्ट करें',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'क्लिपबोर्ड पर कॉपी करें',
    prevPage: 'पिछला',
    nextPage: 'अगला',
    like: 'पसंद',
    dislike: 'नापसंद',
    thankYouForFeedback: 'आपकी प्रतिक्रिया के लिए धन्यवाद!',
  },
  'zh-TW': {
    siteName: 'Presentation Materials',
    tagline: '簡報用免費插圖',
    searchPlaceholder: '搜尋插圖...',
    allCategories: '全部',
    categories: {
      people: '人物',
      business: '商業',
      technology: '科技',
      mark: '標誌',
      education: '教育',
      health: '健康',
      life: '生活',
      building: '建築',
      transportation: '交通',
      nature: '自然',
      foods: '食物',
      fashion: '時尚',
      entertainment: '娛樂',
      other: '其他',
    },
    download: '下載',
    copyToClipboard: '複製到剪貼簿',
    copied: '已複製！',
    downloadFormats: '下載格式',
    noResults: '找不到插圖',
    tryDifferent: '嘗試其他關鍵字或瀏覽類別',
    loading: '載入中...',
    footer: '個人與商業用途免費',
    resultsCount: '找到 {count} 個插圖',
    resultsRange: '第 {start}-{end} 個',
    totalResultsCount: '共 {count} 個插圖',
    tagSearchLabel: '標籤「{tag}」',
    genreSearchLabel: '{genre}',
    quickCopy: '快速複製',
    pasteDirectly: '直接貼上到應用程式',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: '複製到剪貼簿',
    prevPage: '上一頁',
    nextPage: '下一頁',
    like: '讚',
    dislike: '不喜歡',
    thankYouForFeedback: '感謝您的回饋！',
  },
  ru: {
    siteName: 'Presentation Materials',
    tagline: 'Бесплатные иллюстрации для презентаций',
    searchPlaceholder: 'Поиск иллюстраций...',
    allCategories: 'Все',
    categories: {
      people: 'Люди',
      business: 'Бизнес',
      technology: 'Технологии',
      mark: 'Знаки',
      education: 'Образование',
      health: 'Здоровье',
      life: 'Жизнь',
      building: 'Здания',
      transportation: 'Транспорт',
      nature: 'Природа',
      foods: 'Еда',
      fashion: 'Мода',
      entertainment: 'Развлечения',
      other: 'Другое',
    },
    download: 'Скачать',
    copyToClipboard: 'Копировать в буфер',
    copied: 'Скопировано!',
    downloadFormats: 'Форматы загрузки',
    noResults: 'Иллюстрации не найдены',
    tryDifferent: 'Попробуйте другие ключевые слова или просмотрите категории',
    loading: 'Загрузка...',
    footer: 'Бесплатно для личного и коммерческого использования',
    resultsCount: 'Найдено {count} иллюстраций',
    resultsRange: '{start}-{end} из',
    totalResultsCount: 'Всего {count} иллюстраций',
    tagSearchLabel: 'Тег «{tag}»',
    genreSearchLabel: '{genre}',
    quickCopy: 'Быстрое копирование',
    pasteDirectly: 'Вставьте прямо в приложение',
    copyForPowerPoint: 'PowerPoint',
    copyForGoogleDocs: 'Google Docs',
    clipboardSection: 'Копировать в буфер',
    prevPage: 'Назад',
    nextPage: 'Далее',
    like: 'Нравится',
    dislike: 'Не нравится',
    thankYouForFeedback: 'Спасибо за отзыв!',
  },
};

// ============================================
// 投票状態管理用のカスタムフック
// ============================================
const useRatings = () => {
  const STORAGE_KEY = 'illustration-ratings';

  const getRating = (illustrationId) => {
    try {
      const ratings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return ratings[illustrationId] || null;
    } catch {
      return null;
    }
  };

  const setRating = (illustrationId, ratingType) => {
    try {
      const ratings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      ratings[illustrationId] = ratingType;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ratings));
    } catch (error) {
      console.error('Failed to save rating to localStorage:', error);
    }
  };

  return { getRating, setRating };
};

// ============================================
// タグ一覧キャッシュ用のカスタムフック
// ============================================
const useTags = (lang) => {
  const [tagsData, setTagsData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/tags-${lang}.json`);
        const data = await response.json();
        // 新フォーマット (オブジェクト: タグ名 -> 件数) と旧フォーマット (配列) の両方に対応
        if (Array.isArray(data)) {
          // 旧フォーマット: 配列 -> オブジェクトに変換 (件数は0)
          const obj = {};
          data.forEach(tag => { obj[tag] = 0; });
          setTagsData(obj);
        } else {
          setTagsData(data);
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        // Fallback to English
        try {
          const fallbackResponse = await fetch('/tags-en.json');
          const fallbackData = await fallbackResponse.json();
          if (Array.isArray(fallbackData)) {
            const obj = {};
            fallbackData.forEach(tag => { obj[tag] = 0; });
            setTagsData(obj);
          } else {
            setTagsData(fallbackData);
          }
        } catch (fallbackError) {
          console.error('Failed to fetch fallback tags:', fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, [lang]);

  // タグ名の配列 (ソート済み)
  const tags = useMemo(() => Object.keys(tagsData).sort(), [tagsData]);
  // タグ名 -> 件数のマップ
  const tagCounts = tagsData;

  return { tags, tagCounts, tagsLoading: loading };
};

// ============================================
// ジャンル件数キャッシュ用のカスタムフック
// ============================================
const useGenres = (lang) => {
  const [genreData, setGenreData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGenres = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/genres-${lang}.json`);
        if (response.ok) {
          const data = await response.json();
          setGenreData(data);  // { genreName: count, ... }
        } else {
          // ファイルが存在しない場合は空オブジェクト
          setGenreData({});
        }
      } catch (error) {
        console.error('Failed to fetch genres:', error);
        setGenreData({});
      } finally {
        setLoading(false);
      }
    };
    fetchGenres();
  }, [lang]);

  return { genreCounts: genreData, genresLoading: loading };
};

// ============================================
// API呼び出し用のカスタムフック
// ============================================
const useIllustrationsApi = (lang) => {
  const [files, setFiles] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageCache, setPageCache] = useState({});
  const [cursorHistory, setCursorHistory] = useState([null]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const fetchIllustrations = useCallback(async ({ genre, tag, cursor = null, isNewSearch = false }) => {
    setLoading(true);

    // Check cache first (for going back)
    const cacheKey = `${genre || 'all'}-${tag || ''}-${cursor || 'first'}`;
    if (!isNewSearch && pageCache[cacheKey]) {
      setFiles(pageCache[cacheKey].files);
      setNextCursor(pageCache[cacheKey].nextCursor);
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('lang', lang);
      params.append('limit', '30');
      // Capitalize genre for API (e.g., 'building' -> 'Building')
      if (genre && genre !== 'all') {
        const capitalizedGenre = genre.charAt(0).toUpperCase() + genre.slice(1);
        params.append('genre', capitalizedGenre);
      }
      if (tag) params.append('tag', tag);
      if (cursor) params.append('cursor', cursor);

      const response = await fetch(`/api/illustrations?${params}`);
      if (!response.ok) throw new Error('API request failed');

      const data = await response.json();

      setFiles(data.items || []);
      setNextCursor(data.nextCursor);

      // Cache the result
      setPageCache(prev => ({
        ...prev,
        [cacheKey]: {
          files: data.items || [],
          nextCursor: data.nextCursor,
        }
      }));

      if (isNewSearch) {
        setCursorHistory([null]);
        setCurrentPageIndex(0);
      }
    } catch (error) {
      console.error('Failed to fetch illustrations from API:', error);
      setFiles([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [lang, pageCache]);

  const goToNextPage = useCallback((genre, tag) => {
    if (nextCursor) {
      const newIndex = currentPageIndex + 1;
      setCursorHistory(prev => [...prev.slice(0, newIndex + 1), nextCursor]);
      setCurrentPageIndex(newIndex);
      fetchIllustrations({ genre, tag, cursor: nextCursor });
    }
  }, [nextCursor, currentPageIndex, fetchIllustrations]);

  const goToPrevPage = useCallback((genre, tag) => {
    if (currentPageIndex > 0) {
      const newIndex = currentPageIndex - 1;
      const prevCursor = cursorHistory[newIndex];
      setCurrentPageIndex(newIndex);
      fetchIllustrations({ genre, tag, cursor: prevCursor });
    }
  }, [currentPageIndex, cursorHistory, fetchIllustrations]);

  const resetPagination = useCallback(() => {
    setPageCache({});
    setCursorHistory([null]);
    setCurrentPageIndex(0);
    setNextCursor(null);
  }, []);

  return {
    files,
    loading,
    nextCursor,
    currentPageIndex,
    fetchIllustrations,
    goToNextPage,
    goToPrevPage,
    resetPagination,
    hasNextPage: !!nextCursor,
    hasPrevPage: currentPageIndex > 0,
  };
};

// ============================================
// イラストデータ取得用のカスタムフック（統合版）
// ============================================
const useIllustrations = (lang) => {
  const api = useIllustrationsApi(lang);
  const [svgCache, setSvgCache] = useState({});

  // Use illustration objects directly from API (already includes name, category, tags)
  const illustrations = useMemo(() => {
    return api.files.map(item => ({
      file: item.file,
      name: item.name || item.file.replace('.svg', ''),
      category: item.category || 'other',
      tags: item.tags || [],
    }));
  }, [api.files]);

  // Use predefined categories from translations (excluding 'other')
  const allCategories = useMemo(() => {
    const categoryKeys = Object.keys(translations.en.categories).filter(k => k !== 'other');
    return categoryKeys;
  }, []);

  const getSvg = useCallback(async (file) => {
    if (svgCache[file]) {
      return svgCache[file];
    }
    try {
      const response = await fetch(`/illustrations/${file}`);
      const svgText = await response.text();
      setSvgCache(prev => ({ ...prev, [file]: svgText }));
      return svgText;
    } catch (error) {
      console.error('Failed to fetch SVG:', error);
      return null;
    }
  }, [svgCache]);

  return {
    illustrations,
    allCategories,
    getSvg,
    loading: api.loading,
    fetchIllustrations: api.fetchIllustrations,
    goToNextPage: api.goToNextPage,
    goToPrevPage: api.goToPrevPage,
    resetPagination: api.resetPagination,
    hasNextPage: api.hasNextPage,
    hasPrevPage: api.hasPrevPage,
    currentPageIndex: api.currentPageIndex,
  };
};

// ============================================
// モバイルデバイス検出用フック
// ============================================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.matchMedia('(max-width: 768px)').matches;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// ============================================
// ユーティリティ関数
// ============================================
const getLanguage = () => {
  const fullLang = navigator.language; // e.g., 'zh-TW', 'zh-CN', 'en-US'
  const baseLang = fullLang.split('-')[0]; // e.g., 'zh', 'en'

  // Check for exact match first (e.g., 'zh-TW', 'zh-CN')
  if (translations[fullLang]) {
    return fullLang;
  }
  // Handle Chinese fallback: 'zh' -> 'zh-CN'
  if (baseLang === 'zh') {
    return 'zh-CN';
  }
  // Then check for base language (e.g., 'en', 'ja')
  if (translations[baseLang]) {
    return baseLang;
  }
  // Default to English
  return 'en';
};

const useTranslation = () => {
  const [lang, setLang] = useState(getLanguage());
  
  useEffect(() => {
    const handleLanguageChange = () => setLang(getLanguage());
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);
  
  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value?.[k];
    }
    if (typeof value === 'string') {
      return Object.entries(params).reduce(
        (str, [k, v]) => str.replace(`{${k}}`, v),
        value
      );
    }
    return key;
  }, [lang]);
  
  return { t, lang, setLang };
};

// ============================================
// コンポーネント
// ============================================

// サポート言語リスト
const supportedLanguages = [
  { code: 'en', name: 'English' },
  { code: 'ja', name: '日本語' },
  { code: 'zh-CN', name: '简体中文' },
  { code: 'zh-TW', name: '繁體中文' },
  { code: 'ko', name: '한국어' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'ru', name: 'Русский' },
];

// SVGアイコンコンポーネント
const Icon = ({ name, size = 24 }) => {
  const icons = {
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    download: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    copy: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    close: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    clipboard: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>,
    globe: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    chevronDown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
    thumbsUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>,
    thumbsDown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>,
  };
  return icons[name] || null;
};

// 言語セレクターコンポーネント
const LanguageSelector = ({ currentLang, onChangeLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  const currentLanguage = supportedLanguages.find(l => l.code === currentLang) || supportedLanguages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="language-selector" ref={dropdownRef}>
      <button
        className="language-selector-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Icon name="globe" size={18} />
        <span className="language-selector-current">{currentLanguage.name}</span>
        <Icon name="chevronDown" size={16} />
      </button>
      {isOpen && (
        <ul className="language-dropdown" role="listbox">
          {supportedLanguages.map((lang) => (
            <li key={lang.code}>
              <button
                className={`language-option ${currentLang === lang.code ? 'active' : ''}`}
                onClick={() => {
                  onChangeLang(lang.code);
                  setIsOpen(false);
                }}
                role="option"
                aria-selected={currentLang === lang.code}
              >
                {lang.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ヘッダーコンポーネント
const Header = ({ t, currentLang, onChangeLang }) => (
  <header className="header">
    <div className="header-content">
      <div className="header-top">
        <div className="logo">
          <svg width="32" height="32" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="10" y="15" width="80" height="55" rx="3"/>
            <line x1="10" y1="30" x2="90" y2="30"/>
            <circle cx="20" cy="22" r="3" fill="currentColor"/>
            <circle cx="30" cy="22" r="3" fill="currentColor"/>
            <circle cx="40" cy="22" r="3" fill="currentColor"/>
            <path d="M15 85 L50 70 L85 85"/>
          </svg>
          <h1>{t('siteName')}</h1>
        </div>
        <LanguageSelector currentLang={currentLang} onChangeLang={onChangeLang} />
      </div>
      <p className="tagline">{t('tagline')}</p>
    </div>
  </header>
);

// 検索バーコンポーネント（タグサジェスチョン機能付き）
const SearchBar = ({ value, onChange, onTagSelect, placeholder, tags }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Sync inputValue with external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filter tags based on input
  const suggestions = useMemo(() => {
    if (!inputValue || inputValue.length === 0) return [];
    const query = inputValue.toLowerCase();
    return tags
      .filter(tag => tag.toLowerCase().includes(query))
      .slice(0, 10);
  }, [inputValue, tags]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
    // Call onChange to trigger search (debounced in parent)
    onChange(newValue);
  };

  const handleTagClick = (tag) => {
    setInputValue(tag);
    setShowSuggestions(false);
    onTagSelect(tag);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="search-bar-container" ref={containerRef}>
      <div className="search-bar">
        <Icon name="search" size={20} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="search-suggestions">
          {suggestions.map((tag, index) => (
            <li key={`${tag}-${index}`}>
              <button
                className="suggestion-item"
                onClick={() => handleTagClick(tag)}
                type="button"
              >
                <span className="suggestion-hash">#</span>
                {tag}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// カテゴリフィルターコンポーネント
const CategoryFilter = ({ categories, selected, onSelect, t }) => (
  <div className="category-filter">
    <button
      className={`category-btn ${selected === 'all' ? 'active' : ''}`}
      onClick={() => onSelect('all')}
    >
      {t('allCategories')}
    </button>
    {categories.map((cat) => (
      <button
        key={cat}
        className={`category-btn ${selected === cat ? 'active' : ''}`}
        onClick={() => onSelect(cat)}
      >
        {t(`categories.${cat}`)}
      </button>
    ))}
  </div>
);

// イラストカードコンポーネント
const IllustrationCard = ({ illustration, svg, onClick }) => (
  <div className="illustration-card" onClick={() => onClick(illustration)}>
    <div className="card-preview" dangerouslySetInnerHTML={{ __html: svg || '' }} />
    <div className="card-info">
      <span className="card-name">{illustration.name}</span>
    </div>
  </div>
);

// モーダルコンポーネント
const Modal = ({ illustration, svg, description, onClose, onCopyPowerPoint, onCopyGoogleDocs, onDownload, t, copyState, isMobile, onRating, votedRating }) => {
  if (!illustration) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <Icon name="close" size={24} />
        </button>

        <div className="modal-preview" dangerouslySetInnerHTML={{ __html: svg || '' }} />

        <div className="modal-info">
          <h2>{illustration.name}</h2>
          {description && (
            <p className="modal-description">{description}</p>
          )}
          <div className="modal-tags">
            {illustration.tags.map((tag) => (
              <span key={tag} className="tag">#{tag}</span>
            ))}
          </div>
        </div>

        <div className="rating-section">
          {votedRating ? (
            <p className="thank-you-message">
              <Icon name="check" size={18} /> {t('thankYouForFeedback')}
            </p>
          ) : (
            <div className="rating-buttons">
              <button
                className="rating-btn like-btn"
                onClick={() => onRating('like')}
              >
                <Icon name="thumbsUp" size={18} /> {t('like')}
              </button>
              <button
                className="rating-btn dislike-btn"
                onClick={() => onRating('dislike')}
              >
                <Icon name="thumbsDown" size={18} /> {t('dislike')}
              </button>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {!isMobile && (
            <div className="clipboard-section">
              <h3>{t('clipboardSection')}</h3>
              <p className="hint">{t('pasteDirectly')}</p>
              <div className="copy-buttons">
                <button
                  className={`copy-btn ${copyState === 'powerpoint' ? 'copied' : ''}`}
                  onClick={() => onCopyPowerPoint(illustration)}
                >
                  {copyState === 'powerpoint' ? (
                    <><Icon name="check" size={18} /> {t('copied')}</>
                  ) : (
                    <><Icon name="copy" size={18} /> {t('copyForPowerPoint')}</>
                  )}
                </button>
                <button
                  className={`copy-btn ${copyState === 'googledocs' ? 'copied' : ''}`}
                  onClick={() => onCopyGoogleDocs(illustration)}
                >
                  {copyState === 'googledocs' ? (
                    <><Icon name="check" size={18} /> {t('copied')}</>
                  ) : (
                    <><Icon name="copy" size={18} /> {t('copyForGoogleDocs')}</>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="download-section">
            <h3>{t('downloadFormats')}</h3>
            <div className="download-buttons">
              <button onClick={() => onDownload(illustration, 'png')} className="download-btn">
                <Icon name="download" size={16} /> PNG
              </button>
              <button onClick={() => onDownload(illustration, 'svg')} className="download-btn">
                <Icon name="download" size={16} /> SVG
              </button>
              <button onClick={() => onDownload(illustration, 'eps')} className="download-btn">
                <Icon name="download" size={16} /> EPS
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// メインアプリコンポーネント
const App = () => {
  const { t, lang, setLang } = useTranslation();
  const {
    illustrations,
    allCategories,
    getSvg,
    loading,
    fetchIllustrations,
    goToNextPage,
    goToPrevPage,
    resetPagination,
    hasNextPage,
    hasPrevPage,
    currentPageIndex,
  } = useIllustrations(lang);
  const { tags, tagCounts } = useTags(lang);
  const { genreCounts } = useGenres(lang);
  const { getRating, setRating } = useRatings();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIllustration, setSelectedIllustration] = useState(null);
  const [copyState, setCopyState] = useState(null);
  const [svgContents, setSvgContents] = useState({});
  const [currentDescription, setCurrentDescription] = useState('');
  const [searchTag, setSearchTag] = useState(null);
  const [votedRating, setVotedRating] = useState(null);

  // Initial load and language change
  useEffect(() => {
    resetPagination();
    fetchIllustrations({ genre: selectedCategory, tag: searchTag, isNewSearch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Category or search change
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    resetPagination();
    fetchIllustrations({ genre: category, tag: searchTag, isNewSearch: true });
  }, [fetchIllustrations, resetPagination, searchTag]);

  // Search handler (searches by tag) - called on every input change
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    // 検索ボックスが空になったらタグ絞り込みを解除
    if (!query.trim() && searchTag) {
      setSearchTag(null);
      resetPagination();
      fetchIllustrations({ genre: selectedCategory, tag: null, isNewSearch: true });
    }
  }, [searchTag, selectedCategory, fetchIllustrations, resetPagination]);

  // Handler for when a tag is selected from suggestions or search is confirmed
  const handleTagSelect = useCallback((tag) => {
    setSearchQuery(tag);
    setSearchTag(tag);
    resetPagination();
    fetchIllustrations({ genre: selectedCategory, tag, isNewSearch: true });
  }, [fetchIllustrations, resetPagination, selectedCategory]);

  // Pagination handlers
  const handleNextPage = useCallback(() => {
    goToNextPage(selectedCategory, searchTag);
  }, [goToNextPage, selectedCategory, searchTag]);

  const handlePrevPage = useCallback(() => {
    goToPrevPage(selectedCategory, searchTag);
  }, [goToPrevPage, selectedCategory, searchTag]);

  // 説明文を取得
  useEffect(() => {
    const fetchDescription = async () => {
      if (!selectedIllustration) {
        setCurrentDescription('');
        return;
      }
      try {
        // ファイル名から拡張子を除去して説明文ファイル名を生成
        const baseName = selectedIllustration.file.replace('.svg', '');
        const response = await fetch(`/illustrations/${baseName}.${lang}.txt`);
        if (response.ok) {
          const text = await response.text();
          setCurrentDescription(text.trim());
        } else {
          // フォールバック: 英語版を取得
          const fallbackResponse = await fetch(`/illustrations/${baseName}.en.txt`);
          if (fallbackResponse.ok) {
            const text = await fallbackResponse.text();
            setCurrentDescription(text.trim());
          } else {
            setCurrentDescription('');
          }
        }
      } catch (error) {
        console.error('Failed to fetch description:', error);
        setCurrentDescription('');
      }
    };
    fetchDescription();
  }, [selectedIllustration, lang]);

  // 選択されたイラストの投票状態を取得
  useEffect(() => {
    if (selectedIllustration) {
      const existingRating = getRating(selectedIllustration.file);
      setVotedRating(existingRating);
    } else {
      setVotedRating(null);
    }
  }, [selectedIllustration, getRating]);

  // 評価を送信
  const handleRating = useCallback(async (ratingType) => {
    if (!selectedIllustration) return;

    try {
      await fetch('/api/ratings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          illustrationId: selectedIllustration.file,
          ratingType
        })
      });
      setRating(selectedIllustration.file, ratingType);
      setVotedRating(ratingType);
    } catch (error) {
      console.error('Failed to submit rating:', error);
    }
  }, [selectedIllustration, setRating]);

  // SVGコンテンツを取得してキャッシュ
  useEffect(() => {
    const loadSvgs = async () => {
      const contents = {};
      for (const ill of illustrations) {
        const svg = await getSvg(ill.file);
        if (svg) {
          contents[ill.file] = svg;
        }
      }
      setSvgContents(contents);
    };
    if (illustrations.length > 0) {
      loadSvgs();
    }
  }, [illustrations, getSvg]);

  const categories = allCategories;

  // SVGをPNG Blobに変換するヘルパー関数
  const convertSvgToPngBlob = useCallback(async (svgString, size = 512, scale = 2) => {
    // currentColorを黒に置換
    let fullSvg = svgString.replace(/currentColor/g, '#000000');

    // キャンバスサイズ
    const canvasSize = size * scale;

    // SVGをパースしてwidth/heightを設定
    const parser = new DOMParser();
    const doc = parser.parseFromString(fullSvg, 'image/svg+xml');
    const svgElement = doc.documentElement;

    // エラーチェック
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Invalid SVG: ' + parseError.textContent);
    }

    // width/heightを設定
    svgElement.setAttribute('width', canvasSize);
    svgElement.setAttribute('height', canvasSize);

    // XMLシリアライズ
    const serializer = new XMLSerializer();
    const svgData = serializer.serializeToString(svgElement);

    // Base64エンコードしてdata URLを作成
    const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
    const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;

    const canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const img = new Image();

    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load SVG image'));
      img.src = dataUrl;
    });

    // 画像をキャンバスに描画（パディング付き）
    const padding = canvasSize * 0.1;
    const drawSize = canvasSize - padding * 2;
    ctx.drawImage(img, padding, padding, drawSize, drawSize);

    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to create PNG blob'));
      }, 'image/png');
    });

    return pngBlob;
  }, []);

  // PowerPoint用コピー（PNG形式のみ）
  const copyForPowerPoint = useCallback(async (illustration) => {
    try {
      const svgString = svgContents[illustration.file];
      if (!svgString) return;

      const pngBlob = await convertSvgToPngBlob(svgString);

      // PowerPointはPNG形式を直接サポート
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob })
      ]);

      setCopyState('powerpoint');
      setTimeout(() => setCopyState(null), 2000);
    } catch (err) {
      console.error('Failed to copy for PowerPoint:', err);
      alert('クリップボードへのコピーに失敗しました。ダウンロードボタンをお使いください。');
    }
  }, [svgContents, convertSvgToPngBlob]);

  // Google Docs/Slides用コピー（PNG + HTML形式）
  const copyForGoogleDocs = useCallback(async (illustration) => {
    try {
      const svgString = svgContents[illustration.file];
      if (!svgString) return;

      const size = 512;
      const scale = 2;
      const pngBlob = await convertSvgToPngBlob(svgString, size, scale);

      // PNG画像をBase64に変換
      const pngBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(pngBlob);
      });

      // シンプルなHTML形式（imgタグのみ）
      const imageSize = size * scale;
      const htmlContent = `<img src="${pngBase64}" width="${imageSize}" height="${imageSize}" />`;
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });

      // PNG形式とHTML形式の両方をクリップボードに書き込み
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': pngBlob,
          'text/html': htmlBlob
        })
      ]);

      setCopyState('googledocs');
      setTimeout(() => setCopyState(null), 2000);
    } catch (err) {
      console.error('Failed to copy for Google Docs:', err);
      alert('クリップボードへのコピーに失敗しました。ダウンロードボタンをお使いください。');
    }
  }, [svgContents, convertSvgToPngBlob]);

  const downloadFile = useCallback((illustration, format) => {
    const svgString = svgContents[illustration.file];
    if (!svgString) return;
    let blob, filename;
    
    switch (format) {
      case 'svg':
        blob = new Blob([svgString], { type: 'image/svg+xml' });
        filename = `${illustration.name.toLowerCase().replace(/\s+/g, '-')}.svg`;
        break;
      case 'png': {
        // PNGダウンロード用のキャンバス処理
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          canvas.width = 1024;
          canvas.height = 1024;
          ctx.drawImage(img, 0, 0, 1024, 1024);
          canvas.toBlob((pngBlob) => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(pngBlob);
            a.download = `${illustration.name.toLowerCase().replace(/\s+/g, '-')}.png`;
            a.click();
            URL.revokeObjectURL(url);
          }, 'image/png');
        };
        img.src = url;
        return;
      }
      case 'eps': {
        // EPS形式（簡易版 - 実際のプロダクションではサーバーサイド変換推奨）
        const epsContent = `%!PS-Adobe-3.0 EPSF-3.0
%%BoundingBox: 0 0 100 100
%%Title: ${illustration.name}
%%Creator: Presentation Materials
%%EndComments
% SVG content converted to EPS
${svgString}
%%EOF`;
        blob = new Blob([epsContent], { type: 'application/postscript' });
        filename = `${illustration.name.toLowerCase().replace(/\s+/g, '-')}.eps`;
        break;
      }
      default:
        return;
    }
    
    if (blob) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    }
  }, [svgContents]);
  
  return (
    <div className="app">
      <Header t={t} currentLang={lang} onChangeLang={setLang} />
      
      <main className="main-content">
        <div className="search-section">
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            onTagSelect={handleTagSelect}
            placeholder={t('searchPlaceholder')}
            tags={tags}
          />
          <CategoryFilter
            categories={categories}
            selected={selectedCategory}
            onSelect={handleCategoryChange}
            t={t}
          />
        </div>

        <div className="results-info">
          {loading ? (
            <span>{t('loading')}</span>
          ) : (() => {
            // 検索条件ラベルを生成
            const getSearchLabel = () => {
              const labels = [];
              // タグ指定時
              if (searchTag) {
                labels.push(t('tagSearchLabel', { tag: searchTag }));
              }
              // ジャンル指定時
              if (selectedCategory !== 'all') {
                const genreName = t(`categories.${selectedCategory}`);
                labels.push(t('genreSearchLabel', { genre: genreName }));
              }
              return labels.length > 0 ? labels.join(' + ') : null;
            };

            // 件数表示の条件を計算
            const getDisplayCount = () => {
              // タグ+ジャンル両方指定時は件数表示しない
              if (searchTag && selectedCategory !== 'all') {
                return null;
              }
              // タグのみ指定
              if (searchTag) {
                return tagCounts[searchTag];
              }
              // ジャンルのみ指定
              if (selectedCategory !== 'all') {
                const capitalizedGenre = selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1);
                return genreCounts[capitalizedGenre];
              }
              // 条件なし（全体）- 全ジャンルの合計
              const total = Object.values(genreCounts).reduce((sum, count) => sum + count, 0);
              return total > 0 ? total : null;
            };

            const searchLabel = getSearchLabel();
            const displayCount = getDisplayCount();

            // 表示範囲を計算 (1ページ30件)
            const ITEMS_PER_PAGE = 30;
            const rangeStart = currentPageIndex * ITEMS_PER_PAGE + 1;
            const rangeEnd = currentPageIndex * ITEMS_PER_PAGE + illustrations.length;

            return (
              <span>
                {searchLabel && <strong>{searchLabel}: </strong>}
                {displayCount !== null && displayCount !== undefined ? (
                  <>{t('totalResultsCount', { count: displayCount })} / {t('resultsRange', { start: rangeStart, end: rangeEnd })}</>
                ) : (
                  <>{t('resultsRange', { start: rangeStart, end: rangeEnd })}</>
                )}
              </span>
            );
          })()}
        </div>

        {loading ? (
          <div className="loading-state">
            <p>{t('loading')}</p>
          </div>
        ) : illustrations.length > 0 ? (
          <>
            <div className="illustrations-grid">
              {illustrations.map((ill) => (
                <IllustrationCard
                  key={ill.file}
                  illustration={ill}
                  svg={svgContents[ill.file]}
                  onClick={setSelectedIllustration}
                />
              ))}
            </div>
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={handlePrevPage}
                disabled={!hasPrevPage}
              >
                {t('prevPage')}
              </button>
              <button
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={!hasNextPage}
              >
                {t('nextPage')}
              </button>
            </div>
          </>
        ) : (
          <div className="no-results">
            <svg width="80" height="80" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
              <circle cx="42" cy="42" r="30"/>
              <line x1="65" y1="65" x2="90" y2="90"/>
              <line x1="30" y1="35" x2="50" y2="35"/>
              <line x1="35" y1="50" x2="55" y2="50"/>
            </svg>
            <h3>{t('noResults')}</h3>
            <p>{t('tryDifferent')}</p>
          </div>
        )}
      </main>

      <footer className="footer">
        <p>{t('footer')}</p>
      </footer>

      <Modal
        illustration={selectedIllustration}
        svg={selectedIllustration ? svgContents[selectedIllustration.file] : null}
        description={currentDescription}
        onClose={() => setSelectedIllustration(null)}
        onCopyPowerPoint={copyForPowerPoint}
        onCopyGoogleDocs={copyForGoogleDocs}
        onDownload={downloadFile}
        t={t}
        copyState={copyState}
        isMobile={isMobile}
        onRating={handleRating}
        votedRating={votedRating}
      />
    </div>
  );
};

export default App;
